/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import Paginator from '../../util_components/paginator';
import { downloadSampleCSV } from './sample_csv';
import './bulk_tab.css';

const PAGE_LIMIT = 10;
const POLL_INTERVAL_MS = 5000;
const ACTIVE_STATUSES = ['pending', 'processing'];

function BulkTab() {
  const [uploading, setUploading] = useState(false);
  const [rowErrors, setRowErrors] = useState([]);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);

  const [runs, setRuns] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [refreshing, setRefreshing] = useState(false);

  const fileInputRef = useRef(null);

  const loadHistory = useCallback((requestedPage = 1) => {
    const url = get_api_url(`/v2/url_shortner/bulk?page=${requestedPage}`, null, true);
    return get_api(url, true)
      .then((res) => {
        const data = res?.data?.data;
        setRuns(data?.runs || []);
        setTotal(data?.total || 0);
        setLimit(data?.limit || PAGE_LIMIT);
        setPage(data?.page || requestedPage);
      })
      .catch(() => {
        setRuns([]);
        setTotal(0);
      });
  }, []);

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  // Track the current page in a ref so the poll always refreshes whichever page
  // is showing, without racing a page navigation back to a stale page.
  const pageRef = useRef(1);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const anyRunActive = runs.some((run) => ACTIVE_STATUSES.includes(run.status));

  // Poll while any visible run is still pending/processing; stop when none are.
  // Depends on the derived boolean (not the runs array or page) so the interval
  // isn't torn down every poll tick and page changes don't restart it.
  useEffect(() => {
    if (!anyRunActive) {
      return undefined;
    }
    const timer = setInterval(() => {
      loadHistory(pageRef.current);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [anyRunActive, loadHistory]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadHistory(page).finally(() => setRefreshing(false));
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file after a fix
    if (!file) {
      return;
    }
    setRowErrors([]);
    setMessage('');
    setMessageIsError(false);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    const url = get_api_url('/v2/url_shortner/bulk', null, true);

    post_api(url, formData, true, 'formdata')
      .then(() => {
        setUploading(false);
        setMessage('Upload accepted. Generating links — track progress in the table below.');
        setMessageIsError(false);
        loadHistory(1);
      })
      .catch((err) => {
        setUploading(false);
        const resp = err?.response?.data;
        if (resp && Array.isArray(resp.errors) && resp.errors.length > 0) {
          setRowErrors(resp.errors);
        } else {
          setMessage(resp?.reason || resp?.message || 'Upload failed. Please try again.');
          setMessageIsError(true);
        }
      });
  };

  const renderRowCounts = (run) => (
    <span>
      {run.success_rows}
      {' / '}
      {run.total_rows}
      {run.failed_rows > 0 ? (
        <span className="bulk-failed-count"> ({run.failed_rows} failed)</span>
      ) : null}
    </span>
  );

  const renderDownload = (url, label) => {
    if (!url) {
      return <span className="bulk-link-disabled">{label}</span>;
    }
    return (
      <a className="bulk-link" href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  };

  const renderHistoryBody = () => {
    if (runs.length === 0) {
      return (
        <tr>
          <td className="bulk-empty" colSpan={6}>
            No bulk runs yet. Upload a CSV to get started.
          </td>
        </tr>
      );
    }
    return runs.map((run) => (
      <tr key={run.uuid}>
        <td>{run.created_at ? moment(run.created_at).format('DD MMM YYYY, hh:mm A') : '-'}</td>
        <td>{run.created_by_name || '-'}</td>
        <td>{renderRowCounts(run)}</td>
        <td>
          <span className={`bulk-status bulk-status-${run.status}`}>{run.status}</span>
        </td>
        <td>{renderDownload(run.source_url, 'Source')}</td>
        <td>
          {run.status === 'completed'
            ? renderDownload(run.output_url, 'Output')
            : renderDownload('', 'Output')}
        </td>
      </tr>
    ));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="bulk-tab">
      <div className="bulk-actions">
        <button type="button" className="bulk-btn" onClick={downloadSampleCSV}>
          Download sample CSV
        </button>
        <button
          type="button"
          className="bulk-btn bulk-btn-primary"
          onClick={openFilePicker}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Upload CSV'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={onFileSelected}
          style={{ display: 'none' }}
        />
      </div>

      <p className="bulk-hint">
        The CSV must use the sample header. `path` is required; `prefix` ≤ 4 chars, `short_url_tag`
        ≤ 50, `description` ≤ 2000; optional `expire_at` as YYYY-MM-DD (not in the past). Up to
        5,000 rows per file.
      </p>

      {message ? (
        <p className={`bulk-msg${messageIsError ? ' bulk-msg-error' : ''}`}>{message}</p>
      ) : null}

      {rowErrors.length > 0 ? (
        <div className="bulk-errors">
          <div className="bulk-errors-head">
            The file was rejected. Fix these rows and re-upload:
          </div>
          <table className="bulk-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Column</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {rowErrors.map((er) => (
                <tr key={`${er.row}-${er.column}-${er.error}`}>
                  <td>{er.row}</td>
                  <td>{er.column}</td>
                  <td>{er.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="bulk-history">
        <div className="bulk-history-header">
          <div className="bulk-history-title">Bulk runs</div>
          <button type="button" className="bulk-btn" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <table className="bulk-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Created by</th>
              <th>Rows (success / total)</th>
              <th>Status</th>
              <th>Source</th>
              <th>Output</th>
            </tr>
          </thead>
          <tbody>{renderHistoryBody()}</tbody>
        </table>
        {totalPages > 1 ? (
          <Paginator curr_page={page} page_limit={totalPages} pageChange={loadHistory} />
        ) : null}
      </div>
    </div>
  );
}

export default BulkTab;
